import React, { useEffect, useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import { motion } from "framer-motion";
import PaymentButton from "./PaymentButton";

// --- Countdown for limited-time offer (24h from first page load) ---
function useCountdown(hours = 24) {
  const [remaining, setRemaining] = useState(hours * 60 * 60 * 1000);
  useEffect(() => {
    const end = Date.now() + hours * 60 * 60 * 1000;
    const id = setInterval(() => {
      const diff = end - Date.now();
      setRemaining(diff > 0 ? diff : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [hours]);
  const totalSeconds = Math.floor(remaining / 1000);
  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return { hh, mm, ss, remaining };
}

const LimitedOfferBanner = () => {
  const { hh, mm, ss, remaining } = useCountdown(24);
  if (remaining === 0) return null;
  return (
    <div className="w-full bg-red-600/90 text-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm font-semibold">
          <span className="rounded-full bg-black text-white px-2 py-0.5 text-xs tracking-wide">LIMITED-TIME</span>
          <span>Launch offer:</span>
          <span className="line-through opacity-80">₹1299</span>
          <span className="text-black/80">→</span>
          <span className="text-2xl font-extrabold">Free</span>
        </div>
        <div className="text-sm font-bold">Ends in <span className="tabular-nums">{hh}:{mm}:{ss}</span></div>
      </div>
    </div>
  );
};

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative py-16 sm:py-24 ${className}`}>{children}</section>
);

const Glass = ({ children, className = "" }) => (
  <div className={`rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6 sm:p-8 shadow-[0_10px_30px_-10px_rgba(239,68,68,0.35)] ${className}`}>{children}</div>
);

const Arrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13.5 4.5 21 12l-7.5 7.5M21 12H3" />
  </svg>
);

const Check = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-red-500">
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-2.59a.75.75 0 1 0-1.22-.92l-3.887 5.162-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.135-.094l4.472-5.829Z" clipRule="evenodd" />
  </svg>
);

const GradientBG = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#0B1220] to-black" />
    <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-red-500/30 blur-3xl" />
    <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-[#0B1220]/60 blur-3xl" />
    <div className="absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_55%)]" />
  </div>
);

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "career-counseling-webinar";

export default function CC30() {
  const fade = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
  };

  const agenda = [
    { t: "Introduction & Welcome", m: 5, b: ["UniThink intro","How to get maximum value","Ice-breaker: your dream career?"] },
    { t: "The Career Decision Challenge", m: 10, b: ["Common struggles","Why guidance matters","Self-awareness + early planning"] },
    { t: "Structured Path to Career & College", m: 10, b: ["Self-Discovery","Explore options (India & abroad)","Best-fit shortlist","Admissions timeline"] },
    { t: "UniThink’s Guidance Approach", m: 5, b: ["Psychometric (optional)","1:1 counseling + parent updates","End-to-end support, no commissions"] },
    { t: "Interactive Profile Mini-Evaluation", m: 10, b: ["Live poll/quiz","Quick tips from results"] },
    { t: "Q&A + Wrap-up", m: 20, b: ["Personalised answers","Roadmap Template","Next steps + free 1:1 consult"] },
  ];

  const takeaways = [
    "Free 1:1 consultation after the webinar",
    "Profile evaluation with initial feedback",
    "Preview shortlist of suitable programs",
    "Downloadable Career Roadmap Template",
    "Live Q&A with an expert counselor",
    "Recording for all registrants",
    "Exclusive insights from psychometrics & admissions",
  ];

  // 🔥 Step 1: manage form data here
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    question: "",
  });
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    // Double-writing while the CRM proves itself: the Sheet is still the
    // source of truth, so only its outcome decides what the visitor sees.
    const [sheetResult] = await Promise.allSettled([
      fetch("https://script.google.com/macros/s/AKfycbzDt6a4bOBpqeeQtSb5wfEn6D0fHi9ciR_LbwlyPB5A_PUORsg1dK3M5wLAT09ZnHm9hg/exec", {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(formData),
        headers: {
          "Content-Type": "application/json",
        },
      }),
      submitLeadQuietly({ formId: CRM_FORM_ID, fields: { ...formData, _gotcha: gotcha } }),
    ]);
    if (sheetResult.status === "rejected") throw sheetResult.reason;

    alert("Form submitted successfully!");
    setFormData({ name: "", phone: "", email: "", question: "" }); // reset
  } catch (error) {
    alert("Error submitting form.");
    console.error(error);
  }
};


  return (
    <div className="relative text-white">
      <GradientBG />
      <LimitedOfferBanner />

      {/* HERO */}
      <Section id="top" className="pt-6">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold bg-red-500 text-black shadow">Stress‑Free Career & College Guidance Webinar</span>
                  <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
                    What if choosing the right career <span className="text-red-400">could be stress‑free</span>?
                  </h1>
                  <p className="mt-4 text-white/80 text-lg max-w-prose">
                    A heartfelt, practical session for students, parents, and working professionals—guiding you from confusion to clarity with a step‑by‑step roadmap.
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="text-white/70 line-through">₹1299</span>
                    <span className="text-red-400 text-2xl font-extrabold">Free</span>
                    <span className="text-xs text-white/80 bg-white/10 border border-white/15 rounded-full px-2 py-0.5">Limited‑time</span>
                  </div>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <a href="#register" className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-500 px-6 py-3 font-semibold text-black hover:bg-red-400 transition shadow-lg shadow-red-500/30">
                      Book your seat <Arrow />
                    </a>
                    <a href="#agenda" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/15 border border-white/10">
                      See the 60‑min plan
                    </a>
                  </div>
                  <div className="mt-6 text-sm text-white/70">
                    <span className="font-semibold">Date:</span> Saturday, <u>7th September, 2025</u> • 11:00 AM–12:00 PM (IST) • Google Meet
                  </div>
                </motion.div>
      
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-tr from-red-500/15 via-[#0B1220]/20 to-white/10 blur-2xl rounded-3xl" />
                  <Glass className="relative">
                    <div className="aspect-video rounded-xl bg-[radial-gradient(circle_at_30%_30%,rgba(239,68,68,0.25),transparent_40%),radial-gradient(circle_at_70%_60%,rgba(11,18,32,0.28),transparent_45%)] border border-white/10 flex items-center justify-center">
                      <div className="text-center max-w-md">
                        <div className="text-xs uppercase tracking-[0.2em] text-white/70">for students • parents • professionals</div>
                        <div className="mt-3 text-2xl font-bold">Clarity without chaos</div>
                        <p className="mt-2 text-white/75">Align strengths with the right courses & colleges, and craft a calm plan you can follow.</p>
                      </div>
                    </div>
                  </Glass>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    {[
                      {v: "10k+", l: "guided conversations"},
                      {v: "95%", l: "felt more confident"},
                      {v: "50", l: "limited seats"},
                    ].map((s) => (
                      <Glass key={s.l}>
                        <div className="text-center">
                          <div className="text-3xl sm:text-4xl font-extrabold text-white">{s.v}</div>
                          <div className="text-white/70 text-sm mt-1">{s.l}</div>
                        </div>
                      </Glass>
                    ))}
                  </div>
                </motion.div>
              </div>
            </Section>
      {/* ... (hero, benefits, agenda, includes, faq remain unchanged) */}
      {/* BENEFITS */}
            <Section id="benefits">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center max-w-3xl mx-auto">
                  <h2 className="text-3xl sm:text-4xl font-extrabold">Why this session matters</h2>
                  <p className="mt-3 text-white/80">Clear steps. Calm mind. Confident decisions. Walk away with a roadmap you can use right away.</p>
                </motion.div>
      
                <div className="mt-10 grid md:grid-cols-3 gap-6">
                  {[
                    {t: "Increase Self‑Awareness", d: "Identify strengths, interests and goals—so choices feel natural, not forced."},
                    {t: "Demystify Choices", d: "Understand streams, degrees and colleges (India & abroad) with simple frameworks."},
                    {t: "Structured Guidance", d: "Follow a step‑by‑step plan from self‑discovery to admissions—minus the panic."},
                    {t: "Plan Early, Stress Less", d: "Timelines and templates that reduce last‑minute rush and second‑guessing."},
                    {t: "Empowered Decisions", d: "Make informed choices with your family—aligned to strengths and aspirations."},
                    {t: "Student‑First Advice", d: "Transparent, no‑commission counseling focused only on what’s best for you."},
                  ].map((f) => (
                    <motion.div key={f.t} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
                      <Glass>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 text-red-400"><Check /></div>
                          <div>
                            <h3 className="font-semibold text-lg">{f.t}</h3>
                            <p className="text-white/75 mt-1 text-sm">{f.d}</p>
                          </div>
                        </div>
                      </Glass>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Section>
      
            {/* AGENDA + WHAT YOU GET */}
            <Section id="agenda">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <h2 className="text-3xl sm:text-4xl font-extrabold">Your 60‑minute journey</h2>
                  <p className="mt-3 text-white/80">Interactive and personal—designed to replace confusion with clarity.</p>
                  <div className="mt-6 space-y-6 border-l border-white/10 pl-6">
                    {agenda.map((a) => (
                      <div key={a.t} className="relative pl-8">
                        <div className="absolute left-0 top-1 w-2 h-2 rounded-full bg-red-400" />
                        <h4 className="font-semibold text-white">{a.t} <span className="text-white/60 font-normal">({a.m} min)</span></h4>
                        <ul className="mt-2 space-y-1 text-white/80 text-sm list-disc ml-4">
                          {a.b.map((x) => <li key={x}>{x}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
      
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
                  <Glass>
                    <h3 className="text-2xl font-bold">What’s included</h3>
                    <ul className="mt-4 space-y-3">
                      {takeaways.map((t) => (
                        <li key={t} className="flex items-start gap-3"><span className="mt-0.5 text-red-400"><Check /></span><span className="text-white/80">{t}</span></li>
                      ))}
                    </ul>
                    <div className="mt-6 grid sm:grid-cols-2 gap-3">
                      <a href="#register" className="inline-flex items-center justify-center rounded-xl bg-red-500 px-5 py-3 font-semibold text-black hover:bg-red-400 transition">Reserve seat </a>
                      <a href="#faq" className="inline-flex items-center justify-center rounded-xl bg-white/10 px-5 py-3 font-semibold text-white hover:bg-white/15 border border-white/10">FAQ</a>
                    </div>
                  </Glass>
                  <div className="mt-6 text-sm text-white/70">When: Sunday, 7th September 2025 • 11:00 AM–12:00 PM • Google Meet</div>
                </motion.div>
              </div>
            </Section>
      
            {/* INCLUDES / BUNDLES */}
            <Section id="includes">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-center">You’ll leave with…</h2>
                <div className="mt-8 grid md:grid-cols-3 gap-6">
                  {[{t:"Personal clarity kit",l:["Self‑discovery prompts","Strengths & interests map","Next‑step checklist"]},{t:"College shortlist preview",l:["2‑3 program ideas","India & abroad options","Fit based on your profile"]},{t:"Roadmap template (PDF)",l:["Exams + timelines","Application steps","Decision points"]}].map((b)=> (
                    <Glass key={b.t}>
                      <h3 className="font-semibold text-lg">{b.t}</h3>
                      <ul className="mt-3 space-y-2 text-white/80 text-sm">
                        {b.l.map((x)=> (<li key={x} className="flex items-start gap-2"><span className="mt-0.5 text-red-400"><Check/></span>{x}</li>))}
                      </ul>
                    </Glass>
                  ))}
                </div>
              </div>
            </Section>
      
            {/* FAQ */}
            <Section id="faq">
              <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-center">FAQs</h2>
                <div className="mt-8 grid gap-4">
                  {[{q:"Is this useful if I’m in Class 11 or already in college?",a:"Yes. The frameworks apply across stages. You’ll learn how to align strengths with next steps—whether choosing a stream, a major, or a PG program."},{q:"Will I get the recording?",a:"Yes. All registered attendees receive the full recording after the session."},{q:"Is the 1:1 consult actually free?",a:"Absolutely. It’s included for all paid registrants. No obligation to buy anything."},{q:"How do I join?",a:"We’ll email the Google Meet link to your registered email. Please join 5 minutes early."}].map((f)=> (
                    <details key={f.q} className="group rounded-xl border border-white/10 bg-white/5 p-5">
                      <summary className="flex cursor-pointer items-center justify-between font-medium text-white list-none">
                        <span>{f.q}</span>
                        <span className="transition-transform group-open:rotate-90"><Arrow/></span>
                      </summary>
                      <p className="mt-3 text-white/80 text-sm leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            </Section>
      
      

      {/* REGISTER */}
      <Section id="register" className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <Glass>
            <h3 className="text-2xl font-extrabold">Reserve your seat</h3>
            <p className="mt-2 text-white/80 text-sm">Limited seats to keep it interactive and personal.</p>

            {/* Form now bound to state */}
            <form className="mt-6 grid gap-4" onSubmit={(e) => e.preventDefault()}>
              {/* Bot trap: hidden from people, so anything typed here is spam. */}
              <input
                type="text"
                name="_gotcha"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70">Full name</label>
                  <div className="relative">
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1 w-full rounded-xl bg-white/10 border border-white/10 px-4 py-3 pr-10 outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Your name"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center opacity-60">👤</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-white/70">Phone</label>
                  <div className="relative">
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1 w-full rounded-xl bg-white/10 border border-white/10 px-4 py-3 pr-10 outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="+91…"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center opacity-60">📱</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 w-full rounded-xl bg-white/10 border border-white/10 px-4 py-3 pr-10 outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="you@email.com"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center opacity-60">✉️</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70">Your biggest career/college concern</label>
                <textarea
                  name="question"
                  rows={3}
                  value={formData.question}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl bg-white/10 border border-white/10 px-4 py-3 outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Tell us so we can tailor the session…"
                ></textarea>
              </div>
              {/*<div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm text-white/80">
                After submitting, you’ll be redirected to payment (₹99). Meet link will be emailed on confirmation.
              </div>*/}

              {/* Pass data to PaymentButton */}
              {/**<PaymentButton formData={formData} />*/}
               <button
      type="button"
      onClick={handleSubmit}
      className="px-6 py-3 bg-red-500 text-black font-semibold rounded-lg shadow-md hover:bg-red-300 transition"
    >
      Submit
    </button>

              <div className="text-xs text-white/60">
                Need help? Email <a href="mailto:info@unithink.in" className="underline">info@unithink.in</a> or call <a href="tel:+919900911649" className="underline">+91 9900911649</a>.
              </div>
            </form>
          </Glass>

          {/* Sidebar blocks (unchanged) */}
          <div className="space-y-6">
            {/* ... (What happens after / Who is this for) */}
          </div>
        </div>
      </Section>

      <div className="text-center text-xs text-white/50 pb-8">©️ {new Date().getFullYear()} UniThink Education International</div>
    </div>
  );
}
